"use client";

import { createLightbox } from "@/helpers/lightbox";
import NextImage, { getImageProps } from "next/image";
import { useEffect, useId, type CSSProperties } from "react";
import { forceImgReloadOnError } from "@/helpers/forceImgReloadOnError";
import { isAnimatedGif } from "@/components/shared/media/image-format";
import { cn } from "@/lib/utils";

// The lightbox opens an optimized rendition instead of the source file, which
// can be a multi-megabyte PNG. 2048px is sharp at full screen on most displays.
const LIGHTBOX_MAX_WIDTH = 2048;

export default function ImageWithLightbox({
  url,
  width,
  height,
  alt,
  imageClassName,
  imageStyle,
  fullSizeWidth,
  fullSizeHeight,
  quality = 75,
  sizes,
  wrapperClassName,
  unoptimized = isAnimatedGif(url),
  priority = false,
}: {
  url: string;
  width: number;
  height: number;
  fullSizeWidth?: number;
  fullSizeHeight?: number;
  alt: string;
  imageClassName?: string;
  imageStyle?: CSSProperties;
  quality?: number;
  sizes?: string;
  wrapperClassName?: string;
  unoptimized?: boolean;
  priority?: boolean;
}) {
  const imageId = useId();

  const sourceWidth = fullSizeWidth ?? width;
  const sourceHeight = fullSizeHeight ?? height;
  const lightboxWidth = Math.min(sourceWidth, LIGHTBOX_MAX_WIDTH);
  const lightboxHeight = Math.round((lightboxWidth / sourceWidth) * sourceHeight);
  // Without `sizes`, getImageProps picks the 2x candidate, so half the target
  // width resolves to the smallest configured size that covers lightboxWidth.
  const lightboxSrc = getImageProps({
    src: url,
    alt: "",
    width: Math.ceil(lightboxWidth / 2),
    height: Math.ceil(lightboxHeight / 2),
    quality,
    unoptimized,
  }).props.src;

  useEffect(() => {
    let lightboxInstance: any = null;

    // Create and initialize the lightbox
    createLightbox({
      gallery: "#" + imageId,
      children: "a",
      showHideAnimationType: "zoom",
    }).then((instance) => {
      lightboxInstance = instance;
      instance.init();
    });

    // Cleanup function
    return () => {
      if (lightboxInstance) {
        lightboxInstance.destroy();
      }
    };
  }, [imageId]);

  return (
    <span id={imageId} className={wrapperClassName}>
      <a
        href={lightboxSrc}
        data-pswp-width={lightboxWidth}
        data-pswp-height={lightboxHeight}
        target="_blank"
        rel="noreferrer"
      >
        <NextImage
          src={url}
          alt={alt || ""}
          width={width}
          height={height}
          className={cn(
            "rounded-lg overflow-hidden inline-block max-w-full w-full my-10",
            imageClassName,
          )}
          style={imageStyle}
          quality={quality}
          sizes={sizes}
          unoptimized={unoptimized}
          priority={priority}
          onError={forceImgReloadOnError}
        />
      </a>
    </span>
  );
}
